import React, { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';
import {
  CONGRESS_EXPORT_FIELDS,
  DEFAULT_CONGRESS_EXPORT_FIELDS,
  exportCongressToExcel,
  exportCongressToPdf,
  type CongressExportFieldKey
} from '../lib/exportRegistrations';

type FormType = 'congress' | 'symposium' | 'abstract' | 'presentation' | 'symposium_attendees' | 'symposium_presentations' | 'elearning_users' | 'elearning_access';

interface Author {
  name: string;
  institution: string;
}

interface Registration {
  id: string;
  created_at: string;
  title: string;
  author: string;
  email: string;
  phone: string;
  institution: string;
  abstract?: string;
  document_url?: string;
  presentation_type?: string;
  first_name: string;
  last_name: string;
  profession: string;
  license_number: string;
  payment_type: string;
  sponsor_name: string;
  needs_accommodation: boolean;
  accommodation_dates: string[];
  needs_transport: boolean;
  room_type?: string;
  roommate_name?: string;
  presentation_title?: string;
  presentation_author?: string;
  presentation_url?: string;
  license_type?: string;
  attendance_type?: string;
  content?: string;
  corresponding_author?: string;
  coauthors?: Author[] | string;
  keywords?: string;
  hkms_hlk_number?: string;
  accessed_at?: string;
  user_agent?: string;
  ip_address?: string;
  abstract_type?: string;
}

interface Filters {
  dateRange: {
    from: string;
    to: string;
  };
  profession: string;
  paymentType: string;
  accommodation: string;
  transport: string;
  accommodationDates: string[];
  searchTerm: string;
}

interface AbstractModal {
  title: string;
  content: string;
  isOpen: boolean;
}

interface RegistrationDetailModal {
  registration: Registration | null;
  isOpen: boolean;
}

const professionOptions = {
  all: 'Sve profesije',
  nurse: 'Medicinska sestra/tehničar',
  doctor: 'Liječnik',
  pharmacy: 'Farmacija',
  other: 'Ostalo'
};

const paymentOptions = {
  all: 'Svi načini',
  personal: 'Osobno',
  institution: 'Zdravstvena ustanova',
  sponsor: 'Sponzor'
};

const booleanOptions = {
  all: 'Sve',
  yes: 'Da',
  no: 'Ne'
};

export function ReviewPage() {
  const [formType, setFormType] = useState<FormType>('congress');
  const [registrations, setRegistrations] = useState<Registration[]>([]);
  const [filteredRegistrations, setFilteredRegistrations] = useState<Registration[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [abstractModal, setAbstractModal] = useState<AbstractModal>({
    title: '',
    content: '',
    isOpen: false
  });
  const [registrationDetailModal, setRegistrationDetailModal] = useState<RegistrationDetailModal>({
    registration: null,
    isOpen: false
  });
  const [isDeleting, setIsDeleting] = useState(false);
  const [isExportOpen, setIsExportOpen] = useState(false);
  const [selectedExportFields, setSelectedExportFields] = useState<CongressExportFieldKey[]>(
    DEFAULT_CONGRESS_EXPORT_FIELDS
  );
  const [isExporting, setIsExporting] = useState(false);
  const [filters, setFilters] = useState<Filters>({
    dateRange: {
      from: '',
      to: ''
    },
    profession: 'all',
    paymentType: 'all',
    accommodation: 'all',
    transport: 'all',
    accommodationDates: [],
    searchTerm: ''
  });

  const formTitles = {
    congress: 'Prijave za kongres',
    symposium: 'Prijave za simpozij',
    abstract: 'Prijavljeni sažeci',
    presentation: 'Predane prezentacije za kongres',
    symposium_attendees: 'Sudionici simpozija',
    symposium_presentations: 'Prezentacije za simpozij',
    elearning_users: 'E-Learning korisnici',
    elearning_access: 'E-Learning pristupi',
  };

  const fetchRegistrations = async (type: FormType) => {
    setIsLoading(true);
    try {
      let query;
      console.log('Fetching registrations for type:', type);
      
      switch (type) {
        case 'congress':
          query = supabase.from('registrations').select('*');
          break;
        case 'symposium':
          query = supabase.from('symposium_registrations').select('*');
          break;
        case 'abstract':
          query = supabase.from('abstracts').select('*');
          break;
        case 'presentation':
          query = supabase.from('presentations').select('*');
          break;
        case 'symposium_attendees':
          query = supabase.from('symposium_attendees').select('*');
          break;
        case 'symposium_presentations':
          query = supabase.from('symposium_presentations').select('*').order('created_at', { ascending: false });
          break;
        case 'elearning_users':
          query = supabase.from('elearning_users').select('*').order('created_at', { ascending: false });
          break;
        case 'elearning_access':
          query = supabase.from('elearning_access_logs').select('*').order('accessed_at', { ascending: false });
          break;
      }

      const { data, error } = await query;
      if (error) {
        console.error(`Error fetching ${type} data:`, error);
        throw error;
      }

      console.log(`Received ${data?.length || 0} ${type} records:`, data);
      
      // Ako je dohvaćanje bilo uspješno, postavimo podatke u state
      setRegistrations(data || []);
      setFilteredRegistrations(data || []);
    } catch (error) {
      console.error('Error fetching registrations:', error);
      toast.error(`Greška pri dohvaćanju podataka: ${error instanceof Error ? error.message : 'Nepoznata greška'}`);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchRegistrations(formType);
    // Reset filters when changing form type
    setFilters({
      dateRange: {
        from: '',
        to: ''
      },
      profession: 'all',
      paymentType: 'all',
      accommodation: 'all',
      transport: 'all',
      accommodationDates: [],
      searchTerm: ''
    });
    setIsFiltersOpen(false);
  }, [formType]);

  useEffect(() => {
    applyFilters();
  }, [filters, registrations]);

  const applyFilters = () => {
    let filtered = [...registrations];

    // Filter by date range
    if (filters.dateRange.from) {
      const fromDate = new Date(filters.dateRange.from);
      filtered = filtered.filter(reg => new Date(reg.created_at) >= fromDate);
    }
    
    if (filters.dateRange.to) {
      const toDate = new Date(filters.dateRange.to);
      toDate.setHours(23, 59, 59, 999); // End of the day
      filtered = filtered.filter(reg => new Date(reg.created_at) <= toDate);
    }

    // Congress specific filters
    if (formType === 'congress') {
      // Filter by profession
      if (filters.profession !== 'all') {
        filtered = filtered.filter(reg => reg.profession === filters.profession);
      }

      // Filter by payment type
      if (filters.paymentType !== 'all') {
        filtered = filtered.filter(reg => reg.payment_type === filters.paymentType);
      }

      // Filter by accommodation
      if (filters.accommodation !== 'all') {
        const needsAccommodation = filters.accommodation === 'yes';
        filtered = filtered.filter(reg => reg.needs_accommodation === needsAccommodation);
      }

      // Filter by transport
      if (filters.transport !== 'all') {
        const needsTransport = filters.transport === 'yes';
        filtered = filtered.filter(reg => reg.needs_transport === needsTransport);
      }

      // Filter by accommodation dates
      if (filters.accommodationDates.length > 0) {
        filtered = filtered.filter(reg => 
          reg.accommodation_dates?.some(date => filters.accommodationDates.includes(date))
        );
      }
    }

    // Search term filter for all types
    if (filters.searchTerm) {
      const searchLower = filters.searchTerm.toLowerCase();
      filtered = filtered.filter(reg => {
        // Search in different fields based on form type
        if (formType === 'congress') {
          return (
            (reg.first_name?.toLowerCase().includes(searchLower) || false) ||
            (reg.last_name?.toLowerCase().includes(searchLower) || false) ||
            (reg.institution?.toLowerCase().includes(searchLower) || false) ||
            (reg.email?.toLowerCase().includes(searchLower) || false)
          );
        } else {
          return (
            (reg.title?.toLowerCase().includes(searchLower) || false) ||
            (reg.author?.toLowerCase().includes(searchLower) || false) ||
            (reg.institution?.toLowerCase().includes(searchLower) || false) ||
            (reg.email?.toLowerCase().includes(searchLower) || false)
          );
        }
      });
    }

    setFilteredRegistrations(filtered);
  };

  const handleTabChange = (type: FormType) => {
    setFormType(type);
  };

  const toggleExportField = (key: CongressExportFieldKey) => {
    setSelectedExportFields((prev) =>
      prev.includes(key) ? prev.filter((field) => field !== key) : [...prev, key]
    );
  };

  const selectAllExportFields = () => {
    setSelectedExportFields(CONGRESS_EXPORT_FIELDS.map((field) => field.key));
  };

  const clearExportFields = () => {
    setSelectedExportFields([]);
  };

  const handleExport = async (format: 'excel' | 'pdf') => {
    if (selectedExportFields.length === 0) {
      toast.error('Odaberite barem jedno polje za export.');
      return;
    }

    if (filteredRegistrations.length === 0) {
      toast.error('Nema prijava za export.');
      return;
    }

    setIsExporting(true);
    try {
      const stamp = new Date().toISOString().slice(0, 10);
      if (format === 'excel') {
        exportCongressToExcel(
          filteredRegistrations,
          selectedExportFields,
          `prijave-kongres-${stamp}.xlsx`
        );
      } else {
        exportCongressToPdf(
          filteredRegistrations,
          selectedExportFields,
          `prijave-kongres-${stamp}.pdf`
        );
      }
      toast.success(
        `Exportano ${filteredRegistrations.length} prijava u ${format === 'excel' ? 'Excel' : 'PDF'}.`
      );
      setIsExportOpen(false);
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Greška pri exportu. Pokušajte ponovno.');
    } finally {
      setIsExporting(false);
    }
  };

  const handleFilterChange = (
    key: keyof Filters, 
    value: string | string[] | { from: string, to: string }
  ) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleDateChange = (date: string) => {
    setFilters(prev => ({
      ...prev,
      accommodationDates: prev.accommodationDates.includes(date)
        ? prev.accommodationDates.filter(d => d !== date)
        : [...prev.accommodationDates, date]
    }));
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('hr-HR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const downloadDocument = async (url: string, fileName: string, type: string = 'prezentacija') => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      
      // Extract original file name from the URL
      const urlParts = url.split('/');
      const originalFileName = urlParts[urlParts.length - 1];
      
      // Find the last occurrence of underscore followed by file extension
      const lastUnderscoreIndex = originalFileName.lastIndexOf('_');
      const extension = originalFileName.substring(lastUnderscoreIndex + 1);
      
      // Create a clean file name with the original extension
      const cleanFileName = `${type}-${fileName}.${extension}`;
      
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = cleanFileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Error downloading document:', error);
      toast.error('Greška pri preuzimanju dokumenta');
    }
  };

  const resetFilters = () => {
    setFilters({
      dateRange: {
        from: '',
        to: ''
      },
      profession: 'all',
      paymentType: 'all',
      accommodation: 'all',
      transport: 'all',
      accommodationDates: [],
      searchTerm: ''
    });
  };

  const deleteRegistration = async (id: string) => {
    if (!confirm('Jeste li sigurni da želite obrisati ovu prijavu? Ova akcija se ne može poništiti.')) {
      return;
    }

    setIsDeleting(true);
    try {
      let table, bucketName;
      
      // Postavljanje tablice i bucketa ovisno o tipu formulara
      switch (formType) {
        case 'congress':
          table = 'registrations';
          break;
        case 'symposium':
          table = 'symposium_registrations';
          bucketName = 'symposium_documents';
          break;
        case 'abstract':
          table = 'abstracts';
          break;
        case 'presentation':
          table = 'presentations';
          bucketName = 'presentations';
          break;
        case 'symposium_attendees':
          table = 'symposium_attendees';
          break;
        case 'symposium_presentations':
          table = 'symposium_presentations';
          bucketName = 'symposium_presentations';
          break;
        case 'elearning_users':
          table = 'elearning_users';
          break;
        case 'elearning_access':
          table = 'elearning_access_logs';
          break;
        default:
          throw new Error(`Nepodržani tip formulara: ${formType}`);
      }

      // Pronađi registraciju koja se briše
      const registration = registrations.find(r => r.id === id);
      if (!registration) {
        throw new Error('Nije pronađena prijava za brisanje');
      }
      
      console.log(`Brisanje ${formType} zapisa s ID-em:`, id);
      
      // Izbriši odmah zapis iz lokalnog state-a za bolje korisničko iskustvo
      const updatedRegistrations = registrations.filter(r => r.id !== id);
      setRegistrations(updatedRegistrations);
      setFilteredRegistrations(prev => prev.filter(r => r.id !== id));
      
      // 1. Prvo obrišemo zapis iz baze podataka
      const { error } = await supabase
        .from(table)
        .delete()
        .eq('id', id);

      if (error) {
        console.error(`Greška pri brisanju ${formType} zapisa:`, error);
        throw error;
      }
      
      console.log(`Zapis uspješno obrisan iz tablice ${table}`);
      
      // Kratka pauza između brisanja zapisa i datoteke
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // 2. Zatim, ako postoji dokument i bucketName, obriši ga iz storage-a
      if (bucketName && registration.document_url) {
        try {
          // Izvuci ime datoteke iz URL-a
          const urlParts = registration.document_url.split('/');
          const fileName = urlParts[urlParts.length - 1];
          
          console.log(`Brisanje datoteke ${fileName} iz bucketa ${bucketName}`);
          
          const { error: storageError } = await supabase.storage
            .from(bucketName)
            .remove([fileName]);
          
          if (storageError) {
            console.error('Greška pri brisanju datoteke:', storageError);
          } else {
            console.log('Datoteka uspješno obrisana');
          }
        } catch (storageError) {
          console.error('Greška pri obradi brisanja datoteke:', storageError);
        }
      }
      
      toast.success('Prijava je uspješno obrisana');
      
      // Osvježi podatke nakon brisanja
      setTimeout(() => {
        if (formType === 'symposium_presentations') {
          // Za prezentacije simpozija osvježavamo podatke s dužim delayom zbog Supabase propagacije
          setTimeout(() => {
            fetchRegistrations(formType);
          }, 2000);
        } else {
          fetchRegistrations(formType);
        }
      }, 1000);
    } catch (error) {
      console.error('Greška pri brisanju:', error);
      toast.error(`Greška pri brisanju: ${error instanceof Error ? error.message : 'Nepoznata greška'}`);
    } finally {
      setIsDeleting(false);
    }
  };

  const parseCoauthors = (registration: Registration): Author[] => {
    if (!registration.coauthors) return [];
    
    // Ako je string, pokušaj ga parsirati kao JSON
    if (typeof registration.coauthors === 'string') {
      try {
        return JSON.parse(registration.coauthors);
      } catch (error) {
        console.error('Error parsing coauthors:', error);
        return [];
      }
    }
    
    // Ako je već array, vrati ga
    return registration.coauthors as Author[];
  };

  const openRegistrationDetail = (registration: Registration) => {
    setRegistrationDetailModal({
      registration,
      isOpen: true
    });
  };

  const renderTableContent = () => {
    if (isLoading) {
      return (
        <tr>
          <td colSpan={8} className="text-center py-4">
            Učitavanje...
          </td>
        </tr>
      );
    }

    if (!filteredRegistrations || filteredRegistrations.length === 0) {
      return (
        <tr>
          <td colSpan={8} className="text-center py-4">
            Nema pronađenih prijava
          </td>
        </tr>
      );
    }

    return filteredRegistrations.map((registration: any) => {
      if (formType === 'congress') {
        return (
          <tr key={registration.id} className="border-b hover:bg-gray-50 cursor-pointer" onClick={() => openRegistrationDetail(registration)}>
            <td className="py-3 px-4">{new Date(registration.created_at).toLocaleDateString('hr-HR')}</td>
            <td className="py-3 px-4">
              <div>
                <strong>Ime:</strong> {registration.first_name}
              </div>
              <div>
                <strong>Prezime:</strong> {registration.last_name}
              </div>
            </td>
            <td className="py-3 px-4">
              <div>
                <strong>Institucija:</strong> {registration.institution}
              </div>
              <div>
                <strong>Profesija:</strong> {
                  registration.profession ? 
                  (professionOptions[registration.profession as keyof typeof professionOptions] || registration.profession) 
                  : ''
                }
              </div>
              {registration.profession === 'nurse' && (
                <div>
                  <strong>Vrsta licence:</strong> HKMS
                </div>
              )}
              {registration.profession === 'doctor' && (
                <div>
                  <strong>Vrsta licence:</strong> HLK
                </div>
              )}
              {(registration.profession === 'pharmacy' || registration.profession === 'other') && registration.license_type && (
                <div>
                  <strong>Vrsta licence:</strong> {registration.license_type}
                </div>
              )}
              <div>
                <strong>{registration.profession === 'doctor' ? 'HLK broj' : registration.profession === 'nurse' ? 'HKMS broj' : 'Broj licence'}:</strong> {registration.license_number}
              </div>
            </td>
            <td className="py-3 px-4">
              <div>
                <strong>Email:</strong> {registration.email}
              </div>
              <div>
                <strong>Telefon:</strong> {registration.phone}
              </div>
            </td>
            <td className="py-3 px-4">
              <div>
                <strong>Način plaćanja:</strong> {
                  registration.payment_type ?
                  (paymentOptions[registration.payment_type as keyof typeof paymentOptions] || registration.payment_type)
                  : ''
                }
              </div>
              {registration.payment_type === 'sponsor' && (
                <div>
                  <strong>Sponzor:</strong> {registration.sponsor_name}
                </div>
              )}
            </td>
            <td className="py-3 px-4">
              <div>
                <strong>Smještaj:</strong> {registration.needs_accommodation ? 'Da' : 'Ne'}
              </div>
              {registration.needs_accommodation && registration.accommodation_dates?.length > 0 && (
                <div>
                  <strong>Datumi:</strong> {registration.accommodation_dates.join(', ')}
                </div>
              )}
              <div>
                <strong>Prijevoz:</strong> {registration.needs_transport ? 'Da' : 'Ne'}
              </div>
            </td>
            <td className="py-3 px-4">
              <button
                onClick={() => deleteRegistration(registration.id)}
                disabled={isDeleting}
                className="text-red-600 hover:text-red-800 flex items-center"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                Obriši
              </button>
            </td>
          </tr>
        );
      } else if (formType === 'symposium') {
        const coauthors = parseCoauthors(registration);
        
        return (
          <tr key={registration.id} className="border-b hover:bg-gray-50 cursor-pointer" onClick={() => openRegistrationDetail(registration)}>
            <td className="py-3 px-4">{new Date(registration.created_at).toLocaleDateString('hr-HR')}</td>
            <td className="py-3 px-4">
              <div>
                <strong>Naslov predavanja:</strong> {registration.title}
              </div>
            </td>
            <td className="py-3 px-4">
              <div>
                <strong>Glavni autor:</strong> {registration.author}
              </div>
              <div>
                <strong>Institucija:</strong> {registration.institution}
              </div>
              
              {coauthors.length > 0 && (
                <div className="mt-2">
                  <strong className="text-blue-600">Koautori:</strong>
                  <ul className="list-disc list-inside pl-2 text-sm">
                    {coauthors.map((coauthor, idx) => (
                      <li key={idx} className="mt-1">
                        <span className="font-medium">{coauthor.name}</span> 
                        {coauthor.institution && (
                          <span className="text-gray-600"> ({coauthor.institution})</span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </td>
            <td className="py-3 px-4">
              <div>
                <strong>Email:</strong> {registration.email}
              </div>
              <div>
                <strong>Telefon:</strong> {registration.phone}
              </div>
            </td>
            <td className="py-3 px-4">
              <div>
                <strong>Sažetak:</strong>
                <button 
                  onClick={() => setAbstractModal({ 
                    title: registration.title, 
                    content: registration.abstract || '', 
                    isOpen: true 
                  })}
                  className="ml-2 text-blue-600 hover:text-blue-800 underline text-sm"
                >
                  Pregledaj sažetak
                </button>
              </div>
              {registration.keywords && (
                <div className="mt-2">
                  <strong>Ključne riječi:</strong>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {registration.keywords.split(',').map((keyword: string, idx: number) => (
                      <span key={idx} className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                        {keyword.trim()}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </td>
            <td className="py-3 px-4">
              {registration.document_url && (
                <button
                  onClick={() => downloadDocument(
                    registration.document_url!,
                    registration.id,
                    'simpozij'
                  )}
                  className="text-blue-600 hover:text-blue-800 font-medium"
                >
                  Preuzmi prezentaciju
                </button>
              )}
            </td>
            <td className="py-3 px-4">
              <button
                onClick={() => deleteRegistration(registration.id)}
                disabled={isDeleting}
                className="text-red-600 hover:text-red-800 flex items-center"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                Obriši
              </button>
            </td>
          </tr>
        );
      } else if (formType === 'abstract') {
        return (
          <tr key={registration.id} className="border-b hover:bg-gray-50 cursor-pointer" onClick={() => openRegistrationDetail(registration)}>
            <td className="py-3 px-4">{new Date(registration.created_at).toLocaleDateString('hr-HR')}</td>
            <td className="py-3 px-4">
              <div>
                <strong>Naslov rada:</strong> {registration.title}
              </div>
            </td>
            <td className="py-3 px-4">
              <div>
                <strong>Glavni autor:</strong> {registration.corresponding_author}
              </div>
              <div>
                <strong>Institucija:</strong> {registration.institution}
              </div>
              {parseCoauthors(registration).length > 0 && (
                <div className="mt-2">
                  <strong className="text-blue-600">Koautori:</strong>
                  <ul className="list-disc list-inside pl-2 text-sm">
                    {parseCoauthors(registration).map((coauthor, idx) => (
                      <li key={idx} className="mt-1">
                        <span className="font-medium">{coauthor.name}</span> 
                        {coauthor.institution && (
                          <span className="text-gray-600"> ({coauthor.institution})</span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </td>
            <td className="py-3 px-4">
              <div>
                <strong>Email:</strong> {registration.email}
              </div>
              <div>
                <strong>Telefon:</strong> {registration.phone}
              </div>
            </td>
            <td className="py-3 px-4">
              <div>
                <strong>Vrsta prezentacije:</strong> {
                  registration.abstract_type === 'oral' ? 'Usmeno izlaganje' : 'E-poster'
                }
              </div>
            </td>
            <td className="py-3 px-4">
              <div>
                <strong>Sažetak:</strong>
                <button 
                  onClick={() => setAbstractModal({ 
                    title: registration.title, 
                    content: registration.content || '', 
                    isOpen: true 
                  })}
                  className="ml-2 text-blue-600 hover:text-blue-800 underline text-sm"
                >
                  Pregledaj sažetak
                </button>
              </div>
              {registration.keywords && (
                <div className="mt-2">
                  <strong>Ključne riječi:</strong>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {registration.keywords.split(',').map((keyword: string, idx: number) => (
                      <span key={idx} className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                        {keyword.trim()}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </td>
            <td className="py-3 px-4">
              {registration.document_url && (
                <button
                  onClick={() => downloadDocument(
                    registration.document_url!,
                    registration.id,
                    'abstract'
                  )}
                  className="text-blue-600 hover:text-blue-800 font-medium flex items-center"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Preuzmi dokument
                </button>
              )}
            </td>
            <td className="py-3 px-4">
              <button
                onClick={() => deleteRegistration(registration.id)}
                disabled={isDeleting}
                className="text-red-600 hover:text-red-800 flex items-center"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                Obriši
              </button>
            </td>
          </tr>
        );
      } else if (formType === 'presentation') {
        return (
          <tr key={registration.id} className="border-b hover:bg-gray-50 cursor-pointer" onClick={() => openRegistrationDetail(registration)}>
            <td className="py-3 px-4">{new Date(registration.created_at).toLocaleDateString('hr-HR')}</td>
            <td className="py-3 px-4">
              <div>
                <strong>Naslov prezentacije:</strong> {registration.title}
              </div>
            </td>
            <td className="py-3 px-4">
              <div>
                <strong>Autor:</strong> {registration.author}
              </div>
              <div>
                <strong>Institucija:</strong> {registration.institution}
              </div>
            </td>
            <td className="py-3 px-4">
              <div>
                <strong>Email:</strong> {registration.email}
              </div>
              <div>
                <strong>Telefon:</strong> {registration.phone}
              </div>
            </td>
            <td className="py-3 px-4">
              <div>
                <strong>Vrsta:</strong> {registration.presentation_type === 'oral' ? 'Usmeno izlaganje' : 'E-poster'}
              </div>
            </td>
            <td className="py-3 px-4">
              {registration.file_url && (
                <button
                  onClick={() => downloadDocument(
                    registration.file_url,
                    registration.id,
                    'prezentacija'
                  )}
                  className="text-blue-600 hover:text-blue-800 font-medium"
                >
                  Preuzmi prezentaciju
                </button>
              )}
            </td>
            <td className="py-3 px-4">
              <button
                onClick={() => deleteRegistration(registration.id)}
                disabled={isDeleting}
                className="text-red-600 hover:text-red-800 flex items-center"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                Obriši
              </button>
            </td>
          </tr>
        );
      } else if (formType === 'symposium_attendees') {
        return (
          <tr key={registration.id} className="border-b hover:bg-gray-50 cursor-pointer" onClick={() => openRegistrationDetail(registration)}>
            <td className="py-3 px-4">{new Date(registration.created_at).toLocaleDateString('hr-HR')}</td>
            <td className="py-3 px-4">
              <div>
                <strong>Ime i prezime:</strong> {registration.first_name} {registration.last_name}
              </div>
              <div>
                <strong>HKMS/HLK Broj:</strong> {registration.license_number}
              </div>
            </td>
            <td className="py-3 px-4">
              <div>
                <strong>Institucija:</strong> {registration.institution}
              </div>
            </td>
            <td className="py-3 px-4">
              <div>
                <strong>Email:</strong> {registration.email}
              </div>
            </td>
            <td className="py-3 px-4">
              <div>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  registration.attendance_type === 'online' 
                    ? 'bg-blue-100 text-blue-800' 
                    : 'bg-green-100 text-green-800'
                }`}>
                  {registration.attendance_type === 'online' ? 'Online' : 'Uživo'}
                </span>
              </div>
            </td>
            <td className="py-3 px-4">
              <div>
                <strong>Napomena:</strong> {registration.accessed_at ? 'E-Learning pristup' : ''}
              </div>
            </td>
            <td className="py-3 px-4">
              <button
                onClick={() => deleteRegistration(registration.id)}
                disabled={isDeleting}
                className="text-red-600 hover:text-red-800 flex items-center"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                Obriši
              </button>
            </td>
          </tr>
        );
      } else if (formType === 'symposium_presentations') {
        return (
          <tr key={registration.id} className="border-b hover:bg-gray-50 cursor-pointer" onClick={() => openRegistrationDetail(registration)}>
            <td className="py-3 px-4">{new Date(registration.created_at).toLocaleDateString('hr-HR')}</td>
            <td className="py-3 px-4">
              <div>
                <strong>{registration.title}</strong>
              </div>
            </td>
            <td className="py-3 px-4">
              <div>{registration.author}</div>
            </td>
            <td className="py-3 px-4">
              {registration.document_url && (
                <button
                  onClick={() => downloadDocument(
                    registration.document_url!,
                    registration.id,
                    'symposium_presentation'
                  )}
                  className="text-purple-600 hover:text-purple-800 font-medium flex items-center"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Preuzmi prezentaciju
                </button>
              )}
            </td>
            <td className="py-3 px-4">
              <button
                onClick={() => deleteRegistration(registration.id)}
                disabled={isDeleting}
                className="text-red-600 hover:text-red-800 flex items-center"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                Obriši
              </button>
            </td>
          </tr>
        );
      } else if (formType === 'elearning_users') {
        return (
          <tr key={registration.id} className="border-b hover:bg-gray-50 cursor-pointer" onClick={() => openRegistrationDetail(registration)}>
            <td className="py-3 px-4">{formatDate(registration.created_at)}</td>
            <td className="py-3 px-4">
              <div>
                <strong>Ime:</strong> {registration.first_name}
              </div>
              <div>
                <strong>Prezime:</strong> {registration.last_name}
              </div>
            </td>
            <td className="py-3 px-4">
              <div>
                <strong>Email:</strong> {registration.email}
              </div>
            </td>
            <td className="py-3 px-4">
              <div>
                <strong>HKMS/HLK broj:</strong> {registration.hkms_hlk_number}
              </div>
            </td>
            <td className="py-3 px-4">
              <div>
                <strong>Institucija:</strong> {registration.institution}
              </div>
            </td>
            <td className="py-3 px-4">
              <button
                onClick={() => deleteRegistration(registration.id)}
                disabled={isDeleting}
                className="text-red-600 hover:text-red-800 flex items-center"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                Obriši
              </button>
            </td>
          </tr>
        );
      } else if (formType === 'elearning_access') {
        return (
          <tr key={registration.id} className="border-b hover:bg-gray-50 cursor-pointer" onClick={() => openRegistrationDetail(registration)}>
            <td className="py-3 px-4">{formatDate(registration.accessed_at || registration.created_at)}</td>
            <td className="py-3 px-4">
              <div>
                <strong>Ime:</strong> {registration.first_name}
              </div>
              <div>
                <strong>Prezime:</strong> {registration.last_name}
              </div>
            </td>
            <td className="py-3 px-4">
              <div>
                <strong>Email:</strong> {registration.email}
              </div>
            </td>
            <td className="py-3 px-4">
              <div>
                <strong>HKMS/HLK broj:</strong> {registration.hkms_hlk_number}
              </div>
            </td>
            <td className="py-3 px-4">
              <div>
                <strong>Institucija:</strong> {registration.institution}
              </div>
            </td>
            <td className="py-3 px-4">
              <button
                onClick={() => deleteRegistration(registration.id)}
                disabled={isDeleting}
                className="text-red-600 hover:text-red-800 flex items-center"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                Obriši
              </button>
            </td>
          </tr>
        );
      }
    });
  };

  const renderTableHeaders = () => {
    if (formType === 'congress') {
      return (
        <tr>
          <th className="py-3 px-4 text-left">Datum</th>
          <th className="py-3 px-4 text-left">Ime i prezime</th>
          <th className="py-3 px-4 text-left">Institucija i profesija</th>
          <th className="py-3 px-4 text-left">Kontakt</th>
          <th className="py-3 px-4 text-left">Plaćanje</th>
          <th className="py-3 px-4 text-left">Smještaj i prijevoz</th>
          <th className="py-3 px-4 text-left">Akcije</th>
        </tr>
      );
    } else if (formType === 'symposium') {
      return (
        <tr>
          <th className="py-3 px-4 text-left">Datum</th>
          <th className="py-3 px-4 text-left">Naslov</th>
          <th className="py-3 px-4 text-left">Autor i institucija</th>
          <th className="py-3 px-4 text-left">Kontakt</th>
          <th className="py-3 px-4 text-left">Sažetak</th>
          <th className="py-3 px-4 text-left">Prezentacija</th>
          <th className="py-3 px-4 text-left">Akcije</th>
        </tr>
      );
    } else if (formType === 'abstract') {
      return (
        <tr>
          <th className="py-3 px-4 text-left">Datum</th>
          <th className="py-3 px-4 text-left">Naslov</th>
          <th className="py-3 px-4 text-left">Autor i institucija</th>
          <th className="py-3 px-4 text-left">Kontakt</th>
          <th className="py-3 px-4 text-left">Vrsta</th>
          <th className="py-3 px-4 text-left">Sažetak</th>
          <th className="py-3 px-4 text-left">Akcije</th>
        </tr>
      );
    } else if (formType === 'presentation') {
      return (
        <tr>
          <th className="py-3 px-4 text-left">Datum</th>
          <th className="py-3 px-4 text-left">Naslov</th>
          <th className="py-3 px-4 text-left">Autor i institucija</th>
          <th className="py-3 px-4 text-left">Kontakt</th>
          <th className="py-3 px-4 text-left">Vrsta</th>
          <th className="py-3 px-4 text-left">Prezentacija</th>
          <th className="py-3 px-4 text-left">Akcije</th>
        </tr>
      );
    } else if (formType === 'symposium_attendees') {
      return (
        <tr>
          <th className="py-3 px-4 text-left">Datum</th>
          <th className="py-3 px-4 text-left">Ime i prezime</th>
          <th className="py-3 px-4 text-left">Institucija i profesija</th>
          <th className="py-3 px-4 text-left">Kontakt</th>
          <th className="py-3 px-4 text-left">Ostali podaci</th>
          <th className="py-3 px-4 text-left">Napomena</th>
          <th className="py-3 px-4 text-left">Akcije</th>
        </tr>
      );
    } else if (formType === 'symposium_presentations') {
      return (
        <tr>
          <th className="py-3 px-4 text-left">Datum</th>
          <th className="py-3 px-4 text-left">Naslov</th>
          <th className="py-3 px-4 text-left">Autor i institucija</th>
          <th className="py-3 px-4 text-left">Kontakt</th>
          <th className="py-3 px-4 text-left">Prezentacija</th>
          <th className="py-3 px-4 text-left">Akcije</th>
        </tr>
      );
    } else if (formType === 'elearning_users' || formType === 'elearning_access') {
      return (
        <tr>
          <th className="py-3 px-4 text-left">Datum</th>
          <th className="py-3 px-4 text-left">Ime i prezime</th>
          <th className="py-3 px-4 text-left">Email</th>
          <th className="py-3 px-4 text-left">HKMS/HLK broj</th>
          <th className="py-3 px-4 text-left">Institucija</th>
          <th className="py-3 px-4 text-left">Akcije</th>
        </tr>
      );
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      <div className="bg-white rounded-xl shadow-sm p-6 max-w-7xl mx-auto">
        <h1 className="text-2xl font-bold text-gray-800 mb-6">{formTitles[formType]}</h1>
        
        {/* Tabs */}
        <div className="border-b border-gray-200 mb-6">
          <div className="flex space-x-4 overflow-x-auto pb-2">
            <button
              onClick={() => handleTabChange('congress')}
              className={`py-2 px-4 rounded-t-lg font-medium transition-colors ${
                formType === 'congress'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-500 hover:text-blue-600'
              }`}
            >
              Kongres
            </button>
            
            <button
              onClick={() => handleTabChange('symposium_attendees')}
              className={`py-2 px-4 rounded-t-lg font-medium transition-colors ${
                formType === 'symposium_attendees'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-500 hover:text-blue-600'
              }`}
            >
              Sudionici simpozija
            </button>
            
            <button
              onClick={() => handleTabChange('symposium_presentations')}
              className={`py-2 px-4 rounded-t-lg font-medium transition-colors ${
                formType === 'symposium_presentations'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-500 hover:text-blue-600'
              }`}
            >
              Prezentacije simpozij
            </button>
            
            <button
              onClick={() => handleTabChange('symposium')}
              className={`py-2 px-4 rounded-t-lg font-medium transition-colors ${
                formType === 'symposium'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-500 hover:text-blue-600'
              }`}
            >
              Sažeci simpozij
            </button>
            
            <button
              onClick={() => handleTabChange('abstract')}
              className={`py-2 px-4 rounded-t-lg font-medium transition-colors ${
                formType === 'abstract'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-500 hover:text-blue-600'
              }`}
            >
              Sažeci kongres
            </button>
            
            <button
              onClick={() => handleTabChange('presentation')}
              className={`py-2 px-4 rounded-t-lg font-medium transition-colors ${
                formType === 'presentation'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-500 hover:text-blue-600'
              }`}
            >
              Prezentacije kongres
            </button>
            
            <button
              onClick={() => handleTabChange('elearning_users')}
              className={`py-2 px-4 rounded-t-lg font-medium transition-colors ${
                formType === 'elearning_users'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-500 hover:text-blue-600'
              }`}
            >
              E-Learning korisnici
            </button>
            
            <button
              onClick={() => handleTabChange('elearning_access')}
              className={`py-2 px-4 rounded-t-lg font-medium transition-colors ${
                formType === 'elearning_access'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-500 hover:text-blue-600'
              }`}
            >
              E-Learning pristupi
            </button>
          </div>
        </div>

        <div className="mb-8">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold">{formTitles[formType]} ({formType === 'symposium_attendees' ? filteredRegistrations.filter(reg => reg.attendance_type && reg.payment_type).length : filteredRegistrations.length})</h2>
            <div className="flex gap-2">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Pretraži..."
                  value={filters.searchTerm}
                  onChange={(e) => handleFilterChange('searchTerm', e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              {formType === 'congress' && (
                <button
                  type="button"
                  onClick={() => setIsExportOpen(true)}
                  className="flex items-center px-4 py-2 bg-green-50 text-green-700 rounded-lg border border-green-200 hover:bg-green-100"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Export
                </button>
              )}
              <button
                onClick={() => setIsFiltersOpen(!isFiltersOpen)}
                className="flex items-center px-4 py-2 bg-blue-50 text-blue-600 rounded-lg border border-blue-200 hover:bg-blue-100"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M3 3a1 1 0 011-1h12a1 1 0 011 1v3a1 1 0 01-.293.707L12 11.414V15a1 1 0 01-.293.707l-2 2A1 1 0 018 17v-5.586L3.293 6.707A1 1 0 013 6V3z" clipRule="evenodd" />
                </svg>
                Filteri
              </button>
            </div>
          </div>

          {isFiltersOpen && (
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 mb-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Od datuma:</label>
                  <input
                    type="date"
                    value={filters.dateRange.from}
                    onChange={(e) => handleFilterChange('dateRange', { ...filters.dateRange, from: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Do datuma:</label>
                  <input
                    type="date"
                    value={filters.dateRange.to}
                    onChange={(e) => handleFilterChange('dateRange', { ...filters.dateRange, to: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>

                {/* Congress specific filters */}
                {formType === 'congress' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Profesija:</label>
                      <select
                        value={filters.profession}
                        onChange={(e) => handleFilterChange('profession', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      >
                        {Object.entries(professionOptions).map(([value, label]) => (
                          <option key={value} value={value}>{label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Način plaćanja:</label>
                      <select
                        value={filters.paymentType}
                        onChange={(e) => handleFilterChange('paymentType', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      >
                        {Object.entries(paymentOptions).map(([value, label]) => (
                          <option key={value} value={value}>{label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Smještaj:</label>
                      <select
                        value={filters.accommodation}
                        onChange={(e) => handleFilterChange('accommodation', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      >
                        {Object.entries(booleanOptions).map(([value, label]) => (
                          <option key={value} value={value}>{label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Prijevoz:</label>
                      <select
                        value={filters.transport}
                        onChange={(e) => handleFilterChange('transport', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      >
                        {Object.entries(booleanOptions).map(([value, label]) => (
                          <option key={value} value={value}>{label}</option>
                        ))}
                      </select>
                    </div>
                  </>
                )}
              </div>

              {/* Accommodation dates filter for congress */}
              {formType === 'congress' && filters.accommodation === 'yes' && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Datumi smještaja:</label>
                  <div className="flex flex-wrap gap-2">
                    {['25-26.9.2025.', '26-27.9.2025.', '27-28.9.2025.'].map(date => (
                      <div
                        key={date}
                        onClick={() => handleDateChange(date)}
                        className={`
                          cursor-pointer px-3 py-2 rounded border 
                          ${filters.accommodationDates.includes(date) 
                            ? 'bg-blue-500 text-white border-blue-500' 
                            : 'bg-white text-gray-700 border-gray-300 hover:border-blue-300'
                          }
                        `}
                      >
                        {date}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex justify-end">
                <button
                  onClick={resetFilters}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 mr-2"
                >
                  Poništi filtere
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Conditional rendering of tables based on formType */}
        {formType !== 'symposium_attendees' && formType !== 'symposium_presentations' && (
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white shadow-md rounded-lg">
              <thead>
                {renderTableHeaders()}
              </thead>
              <tbody>
                {renderTableContent()}
              </tbody>
            </table>
          </div>
        )}

        {/* Table for symposium attendees */}
        {formType === 'symposium_attendees' && (
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white shadow-md rounded-lg">
              <thead className="bg-gray-100">
                <tr>
                  <th className="py-3 px-4 text-left font-medium text-gray-600">Datum</th>
                  <th className="py-3 px-4 text-left font-medium text-gray-600">Osobni podaci</th>
                  <th className="py-3 px-4 text-left font-medium text-gray-600">Institucija</th>
                  <th className="py-3 px-4 text-left font-medium text-gray-600">Kontakt</th>
                  <th className="py-3 px-4 text-left font-medium text-gray-600">Način sudjelovanja</th>
                  <th className="py-3 px-4 text-left font-medium text-gray-600">Trošak kotizacije</th>
                  <th className="py-3 px-4 text-left font-medium text-gray-600">Akcije</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan={7} className="py-4 text-center text-gray-500">
                      Učitavanje...
                    </td>
                  </tr>
                ) : filteredRegistrations.filter(reg => reg.attendance_type && reg.payment_type).length === 0 ? (
                  <tr>
                    <td colSpan={7} className="py-4 text-center text-gray-500">
                      Nema prijava za prikaz
                    </td>
                  </tr>
                ) : (
                  filteredRegistrations
                    .filter(reg => reg.attendance_type && reg.payment_type)
                    .map((registration) => (
                    <tr key={registration.id} className="border-b">
                      <td className="py-3 px-4">{new Date(registration.created_at).toLocaleDateString('hr-HR')}</td>
                      <td className="py-3 px-4">
                        <div>
                          <strong>Ime i prezime:</strong> {registration.first_name} {registration.last_name}
                        </div>
                        <div>
                          <strong>HKMS/HLK Broj:</strong> {registration.license_number}
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <div>
                          <strong>Institucija:</strong> {registration.institution}
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <div>
                          <strong>Email:</strong> {registration.email}
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <div>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            registration.attendance_type === 'online' 
                              ? 'bg-blue-100 text-blue-800' 
                              : 'bg-green-100 text-green-800'
                          }`}>
                            {registration.attendance_type === 'online' ? 'Online' : 'Uživo'}
                          </span>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <div>
                          <strong>Plaća:</strong> {
                            registration.payment_type === 'personal' ? 'Osobno' :
                            registration.payment_type === 'institution' ? 'Zdravstvena ustanova' :
                            'Sponzor'
                          }
                        </div>
                        {registration.payment_type === 'sponsor' && registration.sponsor_name && (
                          <div>
                            <strong>Sponzor:</strong> {registration.sponsor_name}
                          </div>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <button
                          onClick={() => deleteRegistration(registration.id)}
                          disabled={isDeleting}
                          className="text-red-600 hover:text-red-800 flex items-center"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                          Obriši
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Table for symposium presentations */}
        {formType === 'symposium_presentations' && (
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white shadow-md rounded-lg">
              <thead className="bg-gray-100">
                <tr>
                  <th className="py-3 px-4 text-left font-medium text-gray-600">Datum predaje</th>
                  <th className="py-3 px-4 text-left font-medium text-gray-600">Naslov prezentacije</th>
                  <th className="py-3 px-4 text-left font-medium text-gray-600">Autor</th>
                  <th className="py-3 px-4 text-left font-medium text-gray-600">Prezentacija</th>
                  <th className="py-3 px-4 text-left font-medium text-gray-600">Akcije</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan={5} className="py-4 text-center text-gray-500">
                      Učitavanje...
                    </td>
                  </tr>
                ) : filteredRegistrations.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="py-4 text-center text-gray-500">
                      Nema prezentacija za prikaz
                    </td>
                  </tr>
                ) : (
                  filteredRegistrations.map((registration) => (
                    <tr key={registration.id} className="border-b">
                      <td className="py-3 px-4">{new Date(registration.created_at).toLocaleDateString('hr-HR')}</td>
                      <td className="py-3 px-4">
                        <div>
                          <strong>{registration.title}</strong>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <div>{registration.author}</div>
                      </td>
                      <td className="py-3 px-4">
                        {registration.document_url && (
                          <button
                            onClick={() => downloadDocument(
                              registration.document_url!,
                              registration.id,
                              'symposium_presentation'
                            )}
                            className="text-purple-600 hover:text-purple-800 font-medium flex items-center"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                            </svg>
                            Preuzmi prezentaciju
                          </button>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <button
                          onClick={() => deleteRegistration(registration.id)}
                          disabled={isDeleting}
                          className="text-red-600 hover:text-red-800 flex items-center"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                          Obriši
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Abstract Modal */}
      {abstractModal.isOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setAbstractModal({ ...abstractModal, isOpen: false })}
        >
          <div 
            className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-5 border-b flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-800">{abstractModal.title}</h2>
              <button 
                onClick={() => setAbstractModal({ ...abstractModal, isOpen: false })}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-5 overflow-y-auto flex-grow">
              <div className="whitespace-pre-wrap text-gray-700">{abstractModal.content}</div>
            </div>
            <div className="p-4 border-t flex justify-end">
              <button 
                onClick={() => setAbstractModal({ ...abstractModal, isOpen: false })}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
              >
                Zatvori
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Registration Detail Modal */}
      {registrationDetailModal.isOpen && registrationDetailModal.registration && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setRegistrationDetailModal({ ...registrationDetailModal, isOpen: false })}
        >
          <div 
            className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-5 border-b flex justify-between items-center bg-blue-50">
              <div>
                <span className="inline-block px-3 py-1 bg-blue-600 text-white text-sm rounded-full mb-2">
                  {formTitles[formType]}
                </span>
                <h2 className="text-xl font-bold text-gray-800">
                  {formType === 'congress' 
                    ? `${registrationDetailModal.registration.first_name} ${registrationDetailModal.registration.last_name}` 
                    : registrationDetailModal.registration.title}
                </h2>
                <p className="text-sm text-gray-500">ID: {registrationDetailModal.registration.id}</p>
              </div>
              <button 
                onClick={() => setRegistrationDetailModal({ ...registrationDetailModal, isOpen: false })}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-5 overflow-y-auto flex-grow">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Osnovni podaci */}
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-gray-500">Datum prijave</p>
                    <p className="font-medium">
                      {new Date(registrationDetailModal.registration.created_at).toLocaleDateString('hr-HR', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </p>
                  </div>

                  {formType === 'congress' ? (
                    <>
                      <div>
                        <p className="text-sm text-gray-500">Osobni podaci</p>
                        <p className="font-medium">{registrationDetailModal.registration.first_name} {registrationDetailModal.registration.last_name}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Institucija</p>
                        <p className="font-medium">{registrationDetailModal.registration.institution}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Profesija</p>
                        <p className="font-medium">
                          {professionOptions[registrationDetailModal.registration.profession as keyof typeof professionOptions] || registrationDetailModal.registration.profession}
                        </p>
                      </div>
                      {registrationDetailModal.registration.profession === 'nurse' && (
                        <div>
                          <p className="text-sm text-gray-500">Vrsta licence</p>
                          <p className="font-medium">HKMS</p>
                        </div>
                      )}
                      {registrationDetailModal.registration.profession === 'doctor' && (
                        <div>
                          <p className="text-sm text-gray-500">Vrsta licence</p>
                          <p className="font-medium">HLK</p>
                        </div>
                      )}
                      {(registrationDetailModal.registration.profession === 'pharmacy' || registrationDetailModal.registration.profession === 'other') && registrationDetailModal.registration.license_type && (
                        <div>
                          <p className="text-sm text-gray-500">Vrsta licence</p>
                          <p className="font-medium">{registrationDetailModal.registration.license_type}</p>
                        </div>
                      )}
                      <div>
                        <p className="text-sm text-gray-500">Broj licence</p>
                        <p className="font-medium">{registrationDetailModal.registration.license_number}</p>
                      </div>
                    </>
                  ) : (
                    <>
                      <div>
                        <p className="text-sm text-gray-500">Naslov</p>
                        <p className="font-medium">{registrationDetailModal.registration.title}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Autor</p>
                        <p className="font-medium">
                          {formType === 'abstract' 
                            ? registrationDetailModal.registration.corresponding_author 
                            : registrationDetailModal.registration.author}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Institucija</p>
                        <p className="font-medium">{registrationDetailModal.registration.institution}</p>
                      </div>
                      {formType === 'abstract' && (
                        <div>
                          <p className="text-sm text-gray-500">Vrsta prezentacije</p>
                          <p className="font-medium">
                            {registrationDetailModal.registration.abstract_type === 'oral' ? 'Usmeno izlaganje' : 'E-poster'}
                          </p>
                        </div>
                      )}
                    </>
                  )}
                </div>

                {/* Dodatni podaci */}
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-gray-500">Email</p>
                    <p className="font-medium">{registrationDetailModal.registration.email}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Telefon</p>
                    <p className="font-medium">{registrationDetailModal.registration.phone}</p>
                  </div>

                  {formType === 'congress' && (
                    <>
                      <div>
                        <p className="text-sm text-gray-500">Način plaćanja</p>
                        <p className="font-medium">
                          {paymentOptions[registrationDetailModal.registration.payment_type as keyof typeof paymentOptions] || registrationDetailModal.registration.payment_type}
                        </p>
                      </div>
                      {registrationDetailModal.registration.payment_type === 'sponsor' && (
                        <div>
                          <p className="text-sm text-gray-500">Sponzor</p>
                          <p className="font-medium">{registrationDetailModal.registration.sponsor_name}</p>
                        </div>
                      )}
                      <div>
                        <p className="text-sm text-gray-500">Smještaj</p>
                        <p className="font-medium">{registrationDetailModal.registration.needs_accommodation ? 'Da' : 'Ne'}</p>
                      </div>
                      {registrationDetailModal.registration.needs_accommodation && registrationDetailModal.registration.accommodation_dates?.length > 0 && (
                        <div>
                          <p className="text-sm text-gray-500">Datumi smještaja</p>
                          <p className="font-medium">{registrationDetailModal.registration.accommodation_dates.join(', ')}</p>
                        </div>
                      )}
                      <div>
                        <p className="text-sm text-gray-500">Prijevoz</p>
                        <p className="font-medium">{registrationDetailModal.registration.needs_transport ? 'Da' : 'Ne'}</p>
                      </div>
                    </>
                  )}

                  {(formType === 'symposium' || formType === 'abstract') && registrationDetailModal.registration.keywords && (
                    <div>
                      <p className="text-sm text-gray-500">Ključne riječi</p>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {registrationDetailModal.registration.keywords.split(',').map((keyword: string, idx: number) => (
                          <span key={idx} className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                            {keyword.trim()}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Koautori */}
              {(formType === 'symposium' || formType === 'abstract') && parseCoauthors(registrationDetailModal.registration).length > 0 && (
                <div className="mt-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Koautori</h3>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {parseCoauthors(registrationDetailModal.registration).map((coauthor, idx) => (
                        <div key={idx} className="p-3 bg-white rounded-md shadow-sm">
                          <p className="font-medium">{coauthor.name}</p>
                          {coauthor.institution && (
                            <p className="text-gray-600 text-sm">{coauthor.institution}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Sažetak */}
              {(formType === 'symposium' || formType === 'abstract') && (
                <div className="mt-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Sažetak</h3>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="whitespace-pre-wrap text-gray-700">
                      {formType === 'symposium' 
                        ? registrationDetailModal.registration.abstract
                        : registrationDetailModal.registration.content}
                    </div>
                  </div>
                </div>
              )}

              {/* Dokumenti i preuzimanje */}
              {registrationDetailModal.registration.document_url && (
                <div className="mt-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Dokumenti</h3>
                  <div className="bg-gray-50 p-4 rounded-lg flex items-center">
                    <button
                      onClick={() => downloadDocument(
                        registrationDetailModal.registration!.document_url!,
                        registrationDetailModal.registration!.id,
                        formType === 'symposium' ? 'simpozij' : formType === 'abstract' ? 'abstract' : 'dokument'
                      )}
                      className="text-blue-600 hover:text-blue-800 font-medium flex items-center"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                      Preuzmi dokument
                    </button>
                  </div>
                </div>
              )}
            </div>
            <div className="p-4 border-t flex justify-between">
              <button
                onClick={() => deleteRegistration(registrationDetailModal.registration!.id)}
                disabled={isDeleting}
                className="px-4 py-2 bg-red-50 text-red-600 rounded hover:bg-red-100 transition-colors flex items-center"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                Obriši prijavu
              </button>
              <button 
                onClick={() => setRegistrationDetailModal({ ...registrationDetailModal, isOpen: false })}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
              >
                Zatvori
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Export Modal - Kongres */}
      {isExportOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setIsExportOpen(false)}
        >
          <div
            className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-5 border-b flex justify-between items-center">
              <div>
                <h2 className="text-xl font-bold text-gray-800">Export prijava</h2>
                <p className="text-sm text-gray-500 mt-1">
                  Exportira trenutno filtrirane prijave ({filteredRegistrations.length})
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIsExportOpen(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-5 overflow-y-auto flex-grow">
              <div className="flex flex-wrap gap-2 mb-4">
                <button
                  type="button"
                  onClick={selectAllExportFields}
                  className="px-3 py-1.5 text-sm bg-blue-50 text-blue-700 rounded-lg border border-blue-200 hover:bg-blue-100"
                >
                  Označi sve
                </button>
                <button
                  type="button"
                  onClick={clearExportFields}
                  className="px-3 py-1.5 text-sm bg-gray-50 text-gray-700 rounded-lg border border-gray-200 hover:bg-gray-100"
                >
                  Poništi sve
                </button>
                <button
                  type="button"
                  onClick={() => setSelectedExportFields(DEFAULT_CONGRESS_EXPORT_FIELDS)}
                  className="px-3 py-1.5 text-sm bg-gray-50 text-gray-700 rounded-lg border border-gray-200 hover:bg-gray-100"
                >
                  Zadano
                </button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {CONGRESS_EXPORT_FIELDS.map((field) => {
                  const checked = selectedExportFields.includes(field.key);
                  return (
                    <label
                      key={field.key}
                      className={`flex items-center gap-3 rounded-lg border px-3 py-2 cursor-pointer transition-colors ${
                        checked
                          ? 'border-blue-300 bg-blue-50'
                          : 'border-gray-200 bg-white hover:bg-gray-50'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleExportField(field.key)}
                        className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-800">{field.label}</span>
                    </label>
                  );
                })}
              </div>
            </div>

            <div className="p-4 border-t flex flex-col sm:flex-row gap-2 sm:justify-end">
              <button
                type="button"
                onClick={() => setIsExportOpen(false)}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
              >
                Odustani
              </button>
              <button
                type="button"
                disabled={isExporting}
                onClick={() => handleExport('pdf')}
                className="px-4 py-2 bg-red-50 text-red-700 rounded-lg border border-red-200 hover:bg-red-100 disabled:opacity-50"
              >
                {isExporting ? 'Export...' : 'Export PDF'}
              </button>
              <button
                type="button"
                disabled={isExporting}
                onClick={() => handleExport('excel')}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                {isExporting ? 'Export...' : 'Export Excel'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}